import { Grid2 as Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { DefaultInputPropTypes, useInputHelper } from 'framework/form';
import {
  fetchTemplate,
  MarkdownEditor,
  markdownToHtml,
  PlaceholderValues,
  replacePlaceholders,
  TemplatePreview,
} from 'framework/markdownEditor';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';

type MarkdownEditorWithPreviewProps = DefaultInputPropTypes<string> & {
  templatePlaceholders?: PlaceholderValues;
  defaultTemplateFileName?: string;
  titleAction?: React.ReactNode;
};

const MarkdownEditorWithPreview = ({
  templatePlaceholders = {},
  defaultTemplateFileName,
  titleAction,
  ...props
}: MarkdownEditorWithPreviewProps) => {
  const inputHelper = useInputHelper(props);
  const onInputChange = inputHelper.getOnChangeCallback();
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const { addMessage } = useMessageService();

  const calculatePreviewHtml = useCallback(
    (content: string) => {
      if (content) {
        try {
          const contentWithValues = replacePlaceholders(content, templatePlaceholders);

          const html = markdownToHtml(contentWithValues);
          setPreviewHtml(html);
        } catch (error) {
          inputHelper.setFormFieldErrors(['Incorrect Markdown content']);
          addMessage(new ErrorMessage('Error during rendering Markdown to HTML'));
        }
      }
    },
    [addMessage, inputHelper, templatePlaceholders],
  );

  const handleContentChange = useCallback(
    (content: string) => {
      onInputChange({ target: { value: content } });
      calculatePreviewHtml(content);
    },
    [calculatePreviewHtml, onInputChange],
  );

  useEffect(() => {
    const loadTemplateData = async (templateFileName: string) => {
      try {
        const initialContent = await fetchTemplate({ templateFileName }).then(result => result?.content);
        onInputChange({ target: { value: initialContent } });
      } catch (err) {
        addMessage(new ErrorMessage(`Error loading data: ${err instanceof Error ? err.message : String(err)}`));
      }
    };

    if (!inputHelper.getValue() && defaultTemplateFileName) {
      loadTemplateData(defaultTemplateFileName);
    }
    calculatePreviewHtml(inputHelper.getValue());
  }, [calculatePreviewHtml, defaultTemplateFileName, inputHelper, onInputChange, addMessage]);

  return (
    <Grid container spacing={1} pb={4}>
      <Grid size={6} sx={{ border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
        <MarkdownEditor
          value={inputHelper.getValue()}
          readOnly={inputHelper.getIsDisabled()}
          onChange={handleContentChange}
          editorSx={{ height: '600px' }}
          containerSx={{ height: '600px' }}
        />
      </Grid>
      <Grid size={6} sx={{ border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
        <TemplatePreview
          titleAction={titleAction}
          html={previewHtml}
          containerSx={{ height: '600px' }}
          paperSx={{
            p: 2,
            '& h2, & h1, & h3, & h4': { margin: '24px 0' },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              borderColor: 'border.default',
              margin: '24px 0',
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'border.default',
              padding: '8px',
              textAlign: 'left',
              wordWrap: 'break-word',
            },
            '& th': {
              backgroundColor: 'background.default',
              fontWeight: 'bold',
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

export default MarkdownEditorWithPreview;
